import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setAnswer,
  nextQuestion,
  previousQuestion,
  submitQuizStart,
  saveAnswerStart,
  resetQuiz
} from '../../store/slices/quizSlice';
import { quizQuestions } from '../../data/quizQuestions';
import TextInput from './QuestionTypes/TextInput';
import NumberInput from './QuestionTypes/NumberInput';
import RadioInput from './QuestionTypes/RadioInput';
import TextareaInput from './QuestionTypes/TextareaInput';
import EmailInput from './QuestionTypes/EmailInput';
import PhoneInput from './QuestionTypes/PhoneInput';
import { loadSavedProgress } from '../../store/slices/quizSlice';
import './QuizPage.css';
import { useNavigate } from 'react-router-dom'; 

const QuizPage = () => {
  const navigate = useNavigate(); // v6  
  const dispatch = useDispatch();
  const { currentQuestionIndex, answers, loading, error, isComplete } = useSelector(state => state.quiz);
  const [validationError, setValidationError] = useState('');

  const currentQuestion = quizQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quizQuestions.length - 1;
  const currentAnswer = answers[currentQuestion?.key] || '';

  useEffect(() => {
    // Load any saved progress instead of resetting
    dispatch(loadSavedProgress());
  }, [dispatch]);

  useEffect(() => {
    if (isComplete && !loading && !error) {
      navigate('/report'); // v6
      // history.push('/report'); // v5
    }
  }, [isComplete, loading, error, navigate]);

  const renderQuestionInput = () => {
    const props = {
      question: currentQuestion,
      value: currentAnswer,
      onChange: handleAnswerChange,
      error: validationError
    };

    switch (currentQuestion.type) {
      case 'text':
        return <TextInput {...props} />;
      case 'number':
        return <NumberInput {...props} />;
      case 'radio':
        return <RadioInput {...props} />;
      case 'textarea':
        return <TextareaInput {...props} />;
      case 'email':
        return <EmailInput {...props} />;
      case 'tel':
        return <PhoneInput {...props} />;
      default:
        return <TextInput {...props} />;
    }
  };

  const handleAnswerChange = (value) => {
    setValidationError('');
    dispatch(setAnswer({
      key: currentQuestion.key,
      value: value
    }));
  };

  const validateAnswer = () => {
    if (currentQuestion.required && (!currentAnswer || currentAnswer.toString().trim() === '')) {
      setValidationError('This field is required');
      return false;
    }

    // Additional validation for specific types
    if (currentQuestion.type === 'email' && currentAnswer) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(currentAnswer)) {
        setValidationError('Please enter a valid email address');
        return false;
      }
    }

    if (currentQuestion.type === 'tel' && currentAnswer) {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(currentAnswer)) {
        setValidationError('Please enter a valid 10-digit Indian phone number');
        return false;
      }
    }

    return true;
  };

  // Update handleNext function:
  const handleNext = () => {
    if (!validateAnswer()) {
      return;
    }
    
    if (isLastQuestion) {
      // Submit entire quiz with all answers
      dispatch(submitQuizStart());
    } else {
      // Just move to next question (answer already saved via setAnswer)
      dispatch(nextQuestion());
    }
  };

  const handlePrevious = () => {
    dispatch(previousQuestion());
  };

  if (isComplete) {
    return (
      <div className="quiz-complete">
        <h2>Thank You!</h2>
        <p>Your skincare quiz has been submitted successfully.</p>
        <p>We'll analyze your responses and get back to you with personalized recommendations within 24 hours.</p>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div>Loading...</div>;
  }

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        <div className="quiz-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
            ></div>
          </div>
          <span className="progress-text">
            Question {currentQuestionIndex + 1} of {quizQuestions.length}
          </span>
        </div>

        <div className="quiz-content">
          <h2 className="quiz-question">{currentQuestion.question}</h2>
          {renderQuestionInput()}
          
          {error && <div className="api-error">{error}</div>}
          
          <div className="quiz-navigation">
            <button 
              className="btn-secondary" 
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 || loading}
            >
              Previous
            </button>
            
            <button 
              className="btn-primary" 
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? 'Saving...' : isLastQuestion ? 'Submit Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;